import React, { useRef, useEffect, useCallback, useState } from 'react';
import * as BABYLON from '@babylonjs/core';
import '@babylonjs/loaders';
import { LANE_POSITIONS, ROAD_SPEED_INITIAL, ROAD_SPEED_MAX, ROAD_SPEED_INCREMENT, POWER_UPS, OBSTACLES, createScene, createCamera, createLighting, createRoadSegment, createPlayerMesh, createCoinMesh, createObstacleMesh, createPowerUpMesh,  } from '../engine/GameEngine';
import { GAME_STATUS, PLAYER_STATE } from '../engine/GameState';
import InputManager from '../engine/InputManager';

const ROAD_SEGMENT_DEPTH = 20;
const NUM_ROAD_SEGMENTS = 8;
const SPAWN_DISTANCE = 80;
const DESPAWN_DISTANCE = -20;
const COIN_SPACING = 4;
const OBSTACLE_MIN_GAP = 15;
const JUMP_VELOCITY = 12;
const GRAVITY = -25;
const SLIDE_DURATION = 800;
const LANE_SWITCH_SPEED = 8;

export default function MonkeyRideCanvas({
  gameState,
  onScoreUpdate,
  onCoinsUpdate,
  onDistanceUpdate,
  onGameOver,
  onPowerUpCollect,
  onCrash,
  isPaused,
  characterId,
  vehicleId,
  environmentKey,
}) {
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const sceneRef = useRef(null);
  const playerRef = useRef(null);
  const cameraRef = useRef(null);
  const inputRef = useRef(null);
  const stateRef = useRef({
    lane: 1,
    targetLane: 1,
    laneX: LANE_POSITIONS?.[1],
    playerState: PLAYER_STATE?.RUNNING,
    jumpVelocity: 0,
    playerY: 0,
    slideTimer: 0,
    flyTimer: 0,
    invincible: 0,
    speed: ROAD_SPEED_INITIAL,
    score: 0,
    coins: 0,
    distance: 0,
    frame: 0,
    roadZ: 0,
    roadSegments: [],
    coinMeshes: [],
    obstacleMeshes: [],
    powerUpMeshes: [],
    nextObstacleZ: SPAWN_DISTANCE,
    nextCoinZ: 20,
    nextPowerUpZ: 60,
    activePowerUps: {},
    comboChain: 0,
    comboTimer: 0,
    multiplier: 1,
    lastObstacleLane: -1,
    running: false,
    magnetActive: false,
    shieldActive: false,
    jetpackActive: false,
    speedBoostActive: false,
    powerUpCollectedCount: 0,
  });
  const [isReady, setIsReady] = useState(false);
  const containerRef = useRef(null);

  const handleLaneLeft = useCallback(() => {
    const s = stateRef?.current;
    if (s?.targetLane > 0 && s?.playerState !== PLAYER_STATE?.CRASHED) {
      s.targetLane = s?.targetLane - 1;
    }
  }, []);

  const handleLaneRight = useCallback(() => {
    const s = stateRef?.current;
    if (s?.targetLane < 2 && s?.playerState !== PLAYER_STATE?.CRASHED) {
      s.targetLane = s?.targetLane + 1;
    }
  }, []);

  const handleJump = useCallback(() => {
    const s = stateRef?.current;
    if (s?.playerState === PLAYER_STATE?.RUNNING && s?.playerY <= 0.1) {
      s.playerState = PLAYER_STATE?.JUMPING;
      s.jumpVelocity = JUMP_VELOCITY;
    }
  }, []);

  const handleSlide = useCallback(() => {
    const s = stateRef?.current;
    if (s?.playerState === PLAYER_STATE?.RUNNING) {
      s.playerState = PLAYER_STATE?.SLIDING;
      s.slideTimer = SLIDE_DURATION;
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return;

    const { engine, scene } = createScene(canvas, environmentKey || 'jungle');
    engineRef.current = engine;
    sceneRef.current = scene;

    const camera = createCamera(scene);
    cameraRef.current = camera;

    const { shadowGen } = createLighting(scene);

    // Create player
    const player = createPlayerMesh(scene, characterId || 'classic');
    player.position = new BABYLON.Vector3(LANE_POSITIONS[1], 0, 0);
    playerRef.current = player;
    camera.lockedTarget = player?.getChildMeshes()?.[0] || player;

    // Add player to shadow caster
    player?.getChildMeshes()?.forEach(m => shadowGen?.addShadowCaster(m));

    // Create initial road segments
    const s = stateRef?.current;
    for (let i = 0; i < NUM_ROAD_SEGMENTS; i++) {
      const seg = createRoadSegment(scene, i * ROAD_SEGMENT_DEPTH - 20, environmentKey || 'jungle');
      s?.roadSegments?.push(seg);
    }

    // Input manager
    const input = new InputManager();
    input?.init(canvas);
    input?.on('moveLeft', handleLaneLeft);
    input?.on('moveRight', handleLaneRight);
    input?.on('jump', handleJump);
    input?.on('slide', handleSlide);
    inputRef.current = input;

    // Spawn initial coins
    spawnCoinPattern(scene, s, 20);

    s.running = true;
    setIsReady(true);

    let lastTime = performance.now();

    const gameLoop = () => {
      const now = performance.now();
      const dt = Math.min((now - lastTime) / 1000, 0.05);
      lastTime = now;

      if (!isPaused && s?.running && gameState?.status === GAME_STATUS?.PLAYING) {
        updateGame(dt, scene, player, shadowGen, s);
      }

      scene?.render();
    };

    engine?.runRenderLoop(gameLoop);

    const handleResize = () => engine?.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      s.running = false;
      input?.destroy();
      window.removeEventListener('resize', handleResize);
      engine?.stopRenderLoop();
      scene?.dispose();
      engine?.dispose();
    };
  }, [environmentKey, characterId]);

  // Pause/resume
  useEffect(() => {
    const s = stateRef?.current;
    if (isPaused) s.running = false;
    else if (gameState?.status === GAME_STATUS?.PLAYING) s.running = true;
  }, [isPaused, gameState?.status]);

  function spawnCoinPattern(scene, s, z) {
    const patterns = [
      // Straight line in one lane
      (lane) => [{ lane, z: 0 }, { lane, z: 2 }, { lane, z: 4 }, { lane, z: 6 }],
      // Zigzag across lanes
      () => [{ lane: 0, z: 0 }, { lane: 1, z: 2 }, { lane: 2, z: 4 }, { lane: 1, z: 6 }],
      // Arc pattern
      () => [{ lane: 0, z: 0 }, { lane: 1, z: 1.5 }, { lane: 2, z: 3 }, { lane: 1, z: 4.5 }, { lane: 0, z: 6 }],
      // Double lane
      (lane) => [
        { lane, z: 0 }, { lane, z: 2 }, { lane, z: 4 },
        { lane: (lane + 1) % 3, z: 1 }, { lane: (lane + 1) % 3, z: 3 },
      ],
    ];
    const patternFn = patterns?.[Math.floor(Math.random() * patterns?.length)];
    const lane = Math.floor(Math.random() * 3);
    const coins = patternFn(lane);
    for (const c of coins) {
      const pos = new BABYLON.Vector3(LANE_POSITIONS[c.lane], 0, z + c.z);
      const mesh = createCoinMesh(scene, pos);
      s?.coinMeshes?.push({ mesh, lane: c?.lane, z: z + c?.z, collected: false });
    }
  }

  function spawnObstaclePattern(scene, s, z) {
    const obsType = OBSTACLES?.[Math.floor(Math.random() * OBSTACLES?.length)];
    // Ensure at least one lane is always free
    const blockedLanes = [];
    const numBlocked = Math.random() < 0.3 ? 2 : 1;
    while (blockedLanes?.length < numBlocked) {
      const l = Math.floor(Math.random() * 3);
      if (!blockedLanes?.includes(l)) blockedLanes?.push(l);
    }
    // Make sure we don't block all 3 lanes
    if (blockedLanes?.length >= 3) blockedLanes?.pop();

    for (const lane of blockedLanes) {
      const pos = new BABYLON.Vector3(LANE_POSITIONS[lane], 0, z);
      const mesh = createObstacleMesh(scene, pos, obsType?.id);
      s?.obstacleMeshes?.push({ mesh, lane, z, type: obsType, hit: false });
    }
    s.lastObstacleLane = blockedLanes?.[0];
  }

  function spawnPowerUp(scene, s, z) {
    const pu = POWER_UPS?.[Math.floor(Math.random() * POWER_UPS?.length)];
    const lane = Math.floor(Math.random() * 3);
    const pos = new BABYLON.Vector3(LANE_POSITIONS[lane], 0, z);
    const mesh = createPowerUpMesh(scene, pos, pu?.id);
    s?.powerUpMeshes?.push({ mesh, lane, z, powerUp: pu, collected: false });
  }

  function updateGame(dt, scene, player, shadowGen, s) {
    s.frame++;
    s.speed = Math.min(s?.speed + ROAD_SPEED_INCREMENT, ROAD_SPEED_MAX);
    const moveZ = s?.speed * dt;
    s.distance += moveZ;
    s.roadZ -= moveZ;

    // Score
    const newScore = Math.floor(s?.distance * 2 + s?.coins * 100 * s?.multiplier);
    if (newScore !== s?.score) {
      s.score = newScore;
      onScoreUpdate?.(s?.score);
    }

    // Distance update
    onDistanceUpdate?.(Math.floor(s?.distance));

    // Combo timer
    if (s?.comboTimer > 0) {
      s.comboTimer -= dt * 1000;
      if (s?.comboTimer <= 0) { s.comboChain = 0; s.multiplier = 1; }
    }

    // Power-up timers
    const now = Date.now();
    for (const [puId, expiry] of Object.entries(s?.activePowerUps)) {
      if (now > expiry) {
        delete s?.activePowerUps?.[puId];
        if (puId === 'magnet') s.magnetActive = false;
        if (puId === 'shield') s.shieldActive = false;
        if (puId === 'jetpack') s.jetpackActive = false;
        if (puId === 'speedboost') s.speedBoostActive = false;
        if (puId === 'multiplier') s.multiplier = 1;
      }
    }

    // Lane switching
    const targetX = LANE_POSITIONS?.[s?.targetLane];
    const dx = targetX - s?.laneX;
    if (Math.abs(dx) > 0.05) {
      s.laneX += dx * LANE_SWITCH_SPEED * dt;
    } else {
      s.laneX = targetX;
      s.lane = s?.targetLane;
    }

    // Jump physics
    if (s?.playerState === PLAYER_STATE?.JUMPING || s?.jetpackActive) {
      if (s?.jetpackActive) {
        s.playerY = Math.min(s?.playerY + 5 * dt, 4);
      } else {
        s.jumpVelocity += GRAVITY * dt;
        s.playerY += s?.jumpVelocity * dt;
        if (s?.playerY <= 0) {
          s.playerY = 0;
          s.jumpVelocity = 0;
          s.playerState = PLAYER_STATE?.RUNNING;
        }
      }
    }

    // Slide timer
    if (s?.playerState === PLAYER_STATE?.SLIDING) {
      s.slideTimer -= dt * 1000;
      if (s?.slideTimer <= 0) {
        s.playerState = PLAYER_STATE?.RUNNING;
        s.slideTimer = 0;
      }
    }

    // Invincibility timer
    if (s?.invincible > 0) s.invincible -= dt * 1000;

    // Update player mesh
    player.position.x = s?.laneX;
    player.position.y = s?.playerY;
    player.position.z = 0;

    // Slide visual
    const scaleY = s?.playerState === PLAYER_STATE?.SLIDING ? 0.5 : 1.0;
    player.scaling.y = scaleY;

    // Animate coins
    for (const c of s?.coinMeshes) {
      if (!c?.collected) {
        c.mesh.position.z -= moveZ;
        c.mesh.rotation.y += 2 * dt;
        c.mesh.position.y = 1.0 + Math.sin(s?.frame * 0.05 + c?.z * 0.1) * 0.2;
      }
    }

    // Animate obstacles
    for (const o of s?.obstacleMeshes) {
      if (!o?.hit) o.mesh.position.z -= moveZ;
    }

    // Animate power-ups
    for (const p of s?.powerUpMeshes) {
      if (!p?.collected) {
        p.mesh.position.z -= moveZ;
        p.mesh.rotation.y += 3 * dt;
        p.mesh.position.y = 1.2 + Math.sin(s?.frame * 0.08) * 0.3;
      }
    }

    // Road scrolling
    for (const seg of s?.roadSegments) {
      seg.position.z -= moveZ;
    }

    // Recycle road segments
    for (const seg of s?.roadSegments) {
      if (seg?.position?.z < DESPAWN_DISTANCE) {
        const maxZ = Math.max(...s?.roadSegments?.map(r => r?.position?.z));
        seg.position.z = maxZ + ROAD_SEGMENT_DEPTH;
      }
    }

    // Spawn new obstacles
    const frontZ = SPAWN_DISTANCE;
    if (s?.nextObstacleZ > s?.distance + frontZ - OBSTACLE_MIN_GAP) {
      // not yet
    } else {
      spawnObstaclePattern(scene, s, frontZ);
      s.nextObstacleZ = s?.distance + frontZ + OBSTACLE_MIN_GAP + Math.random() * 10;
    }

    // Spawn coins
    if (s?.nextCoinZ < s?.distance + frontZ) {
      spawnCoinPattern(scene, s, frontZ);
      s.nextCoinZ = s?.distance + frontZ + 8 + Math.random() * 5;
    }

    // Spawn power-ups
    if (s?.nextPowerUpZ < s?.distance + frontZ) {
      spawnPowerUp(scene, s, frontZ);
      s.nextPowerUpZ = s?.distance + frontZ + 40 + Math.random() * 30;
    }

    // Collision detection - coins
    const playerBB = new BABYLON.BoundingBox(
      new BABYLON.Vector3(s.laneX - 0.8, s.playerY, -1.5),
      new BABYLON.Vector3(s.laneX + 0.8, s.playerY + 1.8, 1.5)
    );

    for (const c of s?.coinMeshes) {
      if (c?.collected) continue;
      const coinPos = c?.mesh?.position;
      const dist = Math.sqrt(
        Math.pow(coinPos?.x - s?.laneX, 2) +
        Math.pow(coinPos?.z, 2)
      );
      // Magnet attraction
      if (s?.magnetActive && dist < 6) {
        const dir = new BABYLON.Vector3(s.laneX - coinPos.x, 0, -coinPos.z)?.normalize();
        c?.mesh?.position?.addInPlace(dir?.scale(8 * dt));
      }
      if (dist < 1.2) {
        c.collected = true;
        c?.mesh?.setEnabled(false);
        const coinValue = s?.multiplier;
        s.coins += coinValue;
        s.comboChain++;
        s.comboTimer = 2000;
        if (s?.comboChain >= 5) s.multiplier = Math.min(s?.multiplier + 0.5, 4);
        onCoinsUpdate?.(s?.coins);
      }
    }

    // Collision detection - power-ups
    for (const p of s?.powerUpMeshes) {
      if (p?.collected) continue;
      const dist = Math.sqrt(
        Math.pow(p?.mesh?.position?.x - s?.laneX, 2) +
        Math.pow(p?.mesh?.position?.z, 2)
      );
      if (dist < 1.5) {
        p.collected = true;
        p?.mesh?.setEnabled(false);
        const puId = p?.powerUp?.id;
        s.activePowerUps[puId] = Date.now() + p?.powerUp?.duration;
        if (puId === 'magnet') s.magnetActive = true;
        if (puId === 'shield') s.shieldActive = true;
        if (puId === 'jetpack') { s.jetpackActive = true; s.playerState = PLAYER_STATE?.FLYING; }
        if (puId === 'speedboost') { s.speedBoostActive = true; s.speed = Math.min(s?.speed + 5, ROAD_SPEED_MAX); }
        if (puId === 'multiplier') s.multiplier = 2;
        s.powerUpCollectedCount++;
        onPowerUpCollect?.(puId, p?.powerUp);
      }
    }

    // Collision detection - obstacles
    if (s?.invincible <= 0 && s?.playerState !== PLAYER_STATE?.CRASHED) {
      for (const o of s?.obstacleMeshes) {
        if (o?.hit) continue;
        const obsPos = o?.mesh?.position;
        const distX = Math.abs(obsPos?.x - s?.laneX);
        const distZ = Math.abs(obsPos?.z);
        if (distX < 1.2 && distZ < 1.5) {
          // Check if player can avoid
          const canJumpOver = o?.type?.canJump && s?.playerState === PLAYER_STATE?.JUMPING && s?.playerY > 0.8;
          const canSlideUnder = o?.type?.canSlide && s?.playerState === PLAYER_STATE?.SLIDING;
          if (!canJumpOver && !canSlideUnder && !s?.shieldActive) {
            o.hit = true;
            s.invincible = 2000;
            s.playerState = PLAYER_STATE?.CRASHED;
            onCrash?.();
            setTimeout(() => {
              if (s?.running) {
                s.playerState = PLAYER_STATE?.RUNNING;
                s.invincible = 1500;
              }
            }, 800);
          } else if (s?.shieldActive) {
            o.hit = true;
            delete s?.activePowerUps?.['shield'];
            s.shieldActive = false;
          }
        }
      }
    }

    // Cleanup far-behind objects
    s.coinMeshes = s?.coinMeshes?.filter(c => {
      if (c?.mesh?.position?.z < -10) { c?.mesh?.dispose(); return false; }
      return true;
    });
    s.obstacleMeshes = s?.obstacleMeshes?.filter(o => {
      if (o?.mesh?.position?.z < -10) { o?.mesh?.dispose(); return false; }
      return true;
    });
    s.powerUpMeshes = s?.powerUpMeshes?.filter(p => {
      if (p?.mesh?.position?.z < -10) { p?.mesh?.dispose(); return false; }
      return true;
    });
  }

  return (
    <div ref={containerRef} className="relative w-full" style={{ aspectRatio: '16/9', minHeight: 280 }}>
      <canvas
        ref={canvasRef}
        className="w-full h-full rounded-xl block"
        style={{ touchAction: 'none', outline: 'none' }}
        aria-label="Monkey Ride 3D game canvas - swipe or use arrow keys to play"
      />
      {!isReady && (
        <div className="absolute inset-0 flex items-center justify-center rounded-xl" style={{ background: 'rgba(0,0,0,0.8)' }}>
          <div className="text-center">
            <div className="text-4xl mb-3 animate-bounce">🐒</div>
            <p className="text-white font-bold text-lg">Loading Monkey Ride 3D...</p>
          </div>
        </div>
      )}
    </div>
  );
}
